// CB:BEGIN fr-001-payments-tab-redesign
import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Elements } from '@stripe/react-stripe-js';
import { CardElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import stripePromise from '../../lib/stripe';
import { paymentApi, type PaymentProfile } from '../../lib/paymentApi';

// Card form component
function CardForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const { clientSecret } = await paymentApi.createSetupIntent('card');
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
        }
      });

      if (confirmError) {
        setError(confirmError.message || 'Card validation failed');
        return;
      }

      if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
        await paymentApi.attachPaymentMethod(setupIntent.payment_method as string, true);
        onSuccess();
      } else {
        setError('Setup failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#334155', marginBottom: '8px' }}>
          Card Information
        </label>
        <div style={{ 
          border: '2px solid #e2e8f0', 
          borderRadius: '8px', 
          padding: '16px',
          background: 'white',
          minHeight: '60px',
          boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
        }}
        onMouseDown={(e) => { e.stopPropagation(); }}
        onPointerDown={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); }}
        >
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1e293b',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  '::placeholder': {
                    color: '#94a3b8',
                  },
                },
                invalid: {
                  color: '#dc2626',
                },
              },
              hidePostalCode: false,
            }}
          />
        </div>
      </div>
      
      {error && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#dc2626', 
          borderRadius: '8px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}
      
      <button
        type="submit"
        disabled={!stripe || loading}
        onMouseDown={(e) => { e.stopPropagation(); }}
        onPointerDown={(e) => { e.stopPropagation(); }}
        onClick={(e) => { e.stopPropagation(); }}
        style={{
          padding: '12px 24px',
          background: loading || !stripe ? '#cbd5e1' : '#2563eb',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: loading || !stripe ? 'not-allowed' : 'pointer',
          fontWeight: '600',
          fontSize: '15px',
          transition: 'all 0.2s',
          boxShadow: loading || !stripe ? 'none' : '0 2px 4px rgba(37, 99, 235, 0.2)',
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 1001
        }}
        onMouseEnter={(e) => {
          if (!loading && stripe) {
            e.currentTarget.style.background = '#1d4ed8';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(37, 99, 235, 0.3)';
          }
        }}
        onMouseLeave={(e) => {
          if (!loading && stripe) {
            e.currentTarget.style.background = '#2563eb';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(37, 99, 235, 0.2)';
          }
        }}
      >
        {loading ? 'Adding Card...' : 'Add Card'}
      </button>
    </form>
  );
}

// Bank account form component
function BankAccountForm({ onSuccess, clientSecret }: { onSuccess: () => void; clientSecret: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message || 'Bank account validation failed');
        setLoading(false);
        return;
      }

      const setupIntent = result.setupIntent;
      if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
        await paymentApi.attachPaymentMethod(setupIntent.payment_method as string, false);
        onSuccess();
      } else if (setupIntent?.status === 'requires_action') {
        // Handle microdeposits or other verification
        setError('Bank account verification required. Please check your email or complete the verification process.');
      } else {
        setError('Setup incomplete. Please try again.');
      }
    } catch (err: any) {
      console.error('Bank account setup error:', err);
      setError(err.message || 'Failed to add bank account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#334155', marginBottom: '8px' }}>
          Bank Account Information
        </label>
        <div style={{ 
          border: '1px solid #cbd5e1', 
          borderRadius: '6px', 
          padding: '12px',
          background: 'white',
          minHeight: '120px'
        }}>
          <PaymentElement 
            options={{
              paymentMethodTypes: ['us_bank_account'],
              fields: {
                billingDetails: {
                  email: 'never',
                  phone: 'never',
                },
              },
            }}
          />
        </div>
        <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px', lineHeight: '1.5' }}>
          By adding your bank account, you authorize CoreBids to debit your account for future payments. 
          ACH payments typically take 3-5 business days to process.
        </p>
      </div>
      
      {error && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#dc2626', 
          borderRadius: '8px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}
      
      <button
        type="submit"
        disabled={!stripe || loading}
        style={{
          padding: '10px 20px',
          background: loading || !stripe ? '#cbd5e1' : '#2563eb',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: loading || !stripe ? 'not-allowed' : 'pointer',
          fontWeight: '500',
          fontSize: '14px',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!loading && stripe) e.currentTarget.style.background = '#1d4ed8';
        }}
        onMouseLeave={(e) => {
          if (!loading && stripe) e.currentTarget.style.background = '#2563eb';
        }}
      >
        {loading ? 'Verifying Bank Account...' : 'Add Bank Account'}
      </button>
    </form>
  );
}

export default function PaymentsTab() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [activeForm, setActiveForm] = useState<'card' | 'bank' | null>(null);
  const [paymentProfile, setPaymentProfile] = useState<PaymentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [bankClientSecret, setBankClientSecret] = useState<string | null>(null);
  const [stripeReady, setStripeReady] = useState(false);

  // CB:BEGIN clerk-modal-event-capture-fix
  // Stop Clerk modal from intercepting pointer events
  const stopClerkCapture = (e: any) => {
    e.stopPropagation();
  };
  // CB:END clerk-modal-event-capture-fix

  // Check if Stripe is available
  useEffect(() => {
    stripePromise.then((stripe) => {
      setStripeReady(!!stripe);
      if (!stripe) {
        setError('Stripe is not configured. Please contact support.');
      }
    });
  }, []);

  useEffect(() => {
    if (user) {
      loadPaymentProfile();
    }
  }, [user]);

  const loadPaymentProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Ensure customer exists
      try {
        await paymentApi.ensureCustomer();
      } catch (ensureErr: any) {
        console.warn('Customer ensure warning:', ensureErr);
        // Continue even if ensure fails - might already exist
      }
      
      const profile = await paymentApi.getPaymentProfile();
      setPaymentProfile(profile);
    } catch (err: any) {
      console.error('Failed to load payment profile:', err);
      setError(err.message || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePaymentMethod = async (methodId: string) => {
    if (!confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      setRemoving(methodId);
      await paymentApi.removePaymentMethod(methodId);
      await loadPaymentProfile();
    } catch (err: any) {
      console.error('Failed to remove payment method:', err);
      alert(err.message || 'Failed to remove payment method');
    } finally {
      setRemoving(null);
    }
  };

  const handleAddSuccess = () => {
    setActiveForm(null);
    setBankClientSecret(null);
    loadPaymentProfile();
  };

  const handleBankFormOpen = async () => {
    if (activeForm === 'bank') {
      setActiveForm(null);
      setBankClientSecret(null);
      return;
    }
    
    try {
      setError(null);
      const { clientSecret } = await paymentApi.createSetupIntent('us_bank_account');
      setBankClientSecret(clientSecret);
      setActiveForm('bank');
    } catch (err: any) {
      setError(err.message || 'Failed to initialize bank account form');
    }
  };

  const getCardBrandIcon = (brand: string) => {
    const icons: Record<string, string> = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
    };
    return icons[brand.toLowerCase()] || '💳';
  };

  if (loading) {
    return (
      <>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <div style={{ 
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: '2px solid #cbd5e1',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '12px', fontSize: '14px', color: '#64748b' }}>Loading payment methods...</p>
        </div>
      </>
    );
  }

  return (
    <div 
      style={{ padding: '24px', maxWidth: '600px', pointerEvents: 'auto', position: 'relative', zIndex: 1000 }}
      onPointerDownCapture={stopClerkCapture}
      onMouseDownCapture={stopClerkCapture}
      onClickCapture={stopClerkCapture}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>
        Payment Methods
      </h2>
      <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
        Securely manage your payment methods. All payment information is encrypted and stored securely by Stripe.
      </p>

      {error && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#dc2626', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      {/* Security Notice */}
      <div style={{ 
        padding: '12px 16px', 
        background: '#eff6ff', 
        border: '1px solid #bfdbfe', 
        borderRadius: '8px', 
        marginBottom: '24px',
        fontSize: '13px',
        color: '#1e40af'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontSize: '16px' }}>🔒</span>
          <strong>Secure Payment Processing</strong>
        </div>
        <div style={{ color: '#1e3a8a', marginLeft: '24px' }}>
          Your payment information is encrypted and securely stored by Stripe. We never store your full card or bank account details.
        </div>
      </div>

      {/* Current Payment Methods */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
          Saved Payment Methods
        </h3>
        
        {paymentProfile?.card && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '16px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            marginBottom: '12px',
            background: 'white',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '48px', 
                height: '32px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                color: 'white',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {paymentProfile.card.brand === 'visa' ? 'VISA' : 
                 paymentProfile.card.brand === 'mastercard' ? 'MC' :
                 paymentProfile.card.brand === 'amex' ? 'AMEX' :
                 paymentProfile.card.brand === 'discover' ? 'DISC' : '💳'}
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  {paymentProfile.card.brand.charAt(0).toUpperCase() + paymentProfile.card.brand.slice(1)} •••• {paymentProfile.card.last4}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  Expires {String(paymentProfile.card.expMonth).padStart(2, '0')}/{paymentProfile.card.expYear}
                  {paymentProfile.defaultMethodType === 'card' && (
                    <span style={{ marginLeft: '8px', color: '#10b981', fontWeight: '500' }}>• Default</span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (paymentProfile.card?.id) handleRemovePaymentMethod(paymentProfile.card.id);
              }}
              onMouseDown={(e) => { e.stopPropagation(); }}
              onPointerDown={(e) => { e.stopPropagation(); }}
              disabled={!paymentProfile.card?.id || removing === paymentProfile.card.id}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: '#dc2626',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                cursor: (!paymentProfile.card?.id || removing === paymentProfile.card.id) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                opacity: (!paymentProfile.card?.id || removing === paymentProfile.card.id) ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (paymentProfile.card?.id && removing !== paymentProfile.card.id) {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#f87171';
                }
              }}
              onMouseLeave={(e) => {
                if (paymentProfile.card?.id && removing !== paymentProfile.card.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#fecaca';
                }
              }}
            >
              {removing === paymentProfile.card.id ? 'Removing...' : 'Remove'}
            </button>
          </div>
        )}
        
        {paymentProfile?.bank && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '16px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            marginBottom: '12px',
            background: 'white',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '48px', 
                height: '32px', 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                color: 'white',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                🏦
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                  {paymentProfile.bank.name} ••••{paymentProfile.bank.last4}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  Bank Account
                  {paymentProfile.defaultMethodType === 'us_bank_account' && (
                    <span style={{ marginLeft: '8px', color: '#10b981', fontWeight: '500' }}>• Default</span>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (paymentProfile.bank?.id) handleRemovePaymentMethod(paymentProfile.bank.id);
              }}
              onMouseDown={(e) => { e.stopPropagation(); }}
              onPointerDown={(e) => { e.stopPropagation(); }}
              disabled={!paymentProfile.bank?.id || removing === paymentProfile.bank.id}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: '#dc2626',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                cursor: (!paymentProfile.bank?.id || removing === paymentProfile.bank.id) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                opacity: (!paymentProfile.bank?.id || removing === paymentProfile.bank.id) ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (paymentProfile.bank?.id && removing !== paymentProfile.bank.id) {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#f87171';
                }
              }}
              onMouseLeave={(e) => {
                if (paymentProfile.bank?.id && removing !== paymentProfile.bank.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#fecaca';
                }
              }}
            >
              {removing === paymentProfile.bank.id ? 'Removing...' : 'Remove'}
            </button>
          </div>
        )}
        
        {!paymentProfile?.card && !paymentProfile?.bank && (
          <div style={{ 
            padding: '32px', 
            textAlign: 'center', 
            color: '#64748b',
            fontSize: '14px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px dashed #cbd5e1'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>💳</div>
            <div style={{ fontWeight: '500', marginBottom: '4px', color: '#334155' }}>No payment methods</div>
            <div>Add a payment method below to get started</div>
          </div>
        )}
      </div>

      {/* Add New Payment Method */}
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
          Add Payment Method
        </h3>
        
        {!stripeReady && (
          <div style={{ 
            padding: '16px', 
            background: '#fffbeb', 
            border: '1px solid #fde68a', 
            borderRadius: '8px', 
            marginBottom: '20px',
            fontSize: '14px',
            color: '#92400e'
          }}>
            ⚠️ Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveForm(activeForm === 'card' ? null : 'card');
              if (activeForm === 'bank') setBankClientSecret(null);
            }}
            onMouseDown={(e) => { e.stopPropagation(); }}
            onPointerDown={(e) => { e.stopPropagation(); }}
            disabled={!stripeReady}
            style={{
              flex: 1,
              padding: '14px 20px',
              borderRadius: '8px',
              border: '2px solid',
              borderColor: activeForm === 'card' ? '#2563eb' : '#cbd5e1',
              background: activeForm === 'card' ? '#eff6ff' : 'white',
              color: activeForm === 'card' ? '#2563eb' : '#334155',
              cursor: !stripeReady ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: !stripeReady ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (activeForm !== 'card' && stripeReady) {
                e.currentTarget.style.borderColor = '#94a3b8';
                e.currentTarget.style.background = '#f8fafc';
              }
            }}
            onMouseLeave={(e) => {
              if (activeForm !== 'card' && stripeReady) {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.background = 'white';
              }
            }}
          >
            <span style={{ fontSize: '18px' }}>💳</span>
            <span>Credit Card</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleBankFormOpen();
            }}
            onMouseDown={(e) => { e.stopPropagation(); }}
            onPointerDown={(e) => { e.stopPropagation(); }}
            disabled={!stripeReady}
            style={{
              flex: 1,
              padding: '14px 20px',
              borderRadius: '8px',
              border: '2px solid',
              borderColor: activeForm === 'bank' ? '#2563eb' : '#cbd5e1',
              background: activeForm === 'bank' ? '#eff6ff' : 'white',
              color: activeForm === 'bank' ? '#2563eb' : '#334155',
              cursor: !stripeReady ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              opacity: !stripeReady ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (activeForm !== 'bank' && stripeReady) {
                e.currentTarget.style.borderColor = '#94a3b8';
                e.currentTarget.style.background = '#f8fafc';
              }
            }}
            onMouseLeave={(e) => {
              if (activeForm !== 'bank' && stripeReady) {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.background = 'white';
              }
            }}
          >
            <span style={{ fontSize: '18px' }}>🏦</span>
            <span>Bank Account</span>
          </button>
        </div>

        {activeForm === 'card' && (
          <div style={{ 
            border: '2px solid #e2e8f0', 
            borderRadius: '8px', 
            padding: '24px',
            background: 'white',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onPointerDown={(e) => { e.stopPropagation(); }}
          onClick={(e) => { e.stopPropagation(); }}
          >
            <Elements stripe={stripePromise}>
              <CardForm onSuccess={handleAddSuccess} />
            </Elements>
          </div>
        )}

        {activeForm === 'bank' && bankClientSecret && (
          <div style={{ 
            border: '2px solid #e2e8f0', 
            borderRadius: '8px', 
            padding: '24px',
            background: 'white',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onPointerDown={(e) => { e.stopPropagation(); }}
          onClick={(e) => { e.stopPropagation(); }}
          >
            <Elements stripe={stripePromise} options={{ clientSecret: bankClientSecret }}>
              <BankAccountForm onSuccess={handleAddSuccess} clientSecret={bankClientSecret} />
            </Elements>
          </div>
        )}

        {activeForm === 'bank' && !bankClientSecret && (
          <div style={{ 
            padding: '16px', 
            background: '#f8fafc', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '14px'
          }}>
            Loading bank account form...
          </div>
        )}
      </div>
    </div>
  );
}
// CB:END fr-001-payments-tab-redesign
